import { CoreLogService } from "./log.service";
import { ValueStorageDataModel } from "../shared/models/valueStorage.model";
import { Observable, Subject, Subscription } from "rxjs";
import { OnDestroy, Injectable } from "@angular/core";
import Utils from "../shared/utils";
import { CircularInjectionHelper } from "./circularInjectionHelper";
import { ContentType } from "../shared/contentTypes";
import { IObjectObserver } from "./IObjectObserver";
import { ObjectObserverFactory } from "./objectObserverFactory";

/** Speicher Bereich */
export const enum StorageHive {
  Local,
  // Server // todo momenty no server synced storage
}

/** Enum für Keys die sich im Speicher hinterlegt werden */
export const enum LocalKeys {
  yourAppAuthData = "yourAppAuthData",
  yourAppUserData = "yourAppUserData",
  yourAppDeviceToken = "yourAppDeviceToken",
  yourAppDeviceFCMToken = "yourAppDeviceFCMToken",
  layoutClass = "layoutClass",
  languageChoice = "languageChoice",
  lastSendLanguage = "lastSendLanguage",
  pushChoice = "pushChoice",
  soundChoice = "soundChoice",
  webVoucherBalance = "webVoucherBalance",
  bonusPointViewModel = "bonusPointViewModel",
  versionData = "versionData",
  BonuspointsDataModel = "BonuspointsDataModel",
  WebshopBasket = "WebshopBasket",
  WebshopBasketTime = "WebshopBasketTime",
  WebshopBaseData = "WebshopBaseData",
  WebshopLastOrderInfo = "WebshopLastOrderInfo",
  storeVersion = "storeVersion",
  storeHypCusNr = "storeHypCusNr",
  cookieConsent = "cookieConsent",
  cookieConsentMeta = "cookieConsentMeta",
  sotLocation = "sotLocation",
  sotAllProductDetailsCall = "sotAllProductDetailsCall",
  ItemTags = "itemTags",
  maxConfirmedAge = "maxConfirmedAge",
  inHouseOrder = "inHouseOrder",
  remoteGroupOrder = "remoteGroupOrder",
  groupSummary = "groupSummary",
  sockedUrl = "sockedUrl",
  bannerBeenClosed = "bannerBeenClosed",
  deliveryPostCode = "deliveryPostCode",
  deliveryCity = "deliveryCity",
  deliveryStreet = "deliveryStreet",
  houseNumber = "houseNumber",
  serviceTime = "serviceTime",
  benefitCache = "benefitCache",

  gtc = "gtc",
  newsletter_accepted = "newsletter_accepted",

  reservedCoupon = "reservedCoupon",
  basketCouponRowProductId = "basketCouponRowProductId",
}

/** LocalKeys welche an den eingelogten Benutzer gebunden sind, und beim ausloggen gelöscht werden */
export const UserLocalKeys: LocalKeys[] = [
  LocalKeys.yourAppAuthData,
  LocalKeys.webVoucherBalance,
  LocalKeys.bonusPointViewModel,
  LocalKeys.reservedCoupon,
  LocalKeys.basketCouponRowProductId,
]

export class HiveAndKey {
  public hive: StorageHive;
  public key: String;

  constructor(val_hive: StorageHive, val_key: String) {
    this.hive = val_hive;
    this.key = val_key;
  }
}

/** ValueStorage für Daten/Variablen */
@Injectable()
export class ValueStorageService implements OnDestroy {

  private _observerChanged: Subject<HiveAndKey[]> = new Subject();
  private _localValueStorageObsServ: IObjectObserver<ValueStorageDataModel>;
  private localValueStorageChangesSubscriber: Subscription;

  /** Environment Storage
 * @param _coreLogService CoreLogService
 * @param objObsInjectHelpers ObjectObseverInjectionHelper
 */
  constructor(private _coreLogService: CoreLogService, objObsInjectHelpers: CircularInjectionHelper) {
    const me = this;
    me._localValueStorageObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<ValueStorageDataModel>(ContentType.Local_ValueStorage, objObsInjectHelpers);

    me.localValueStorageChangesSubscriber = me._localValueStorageObsServ.ObserveChanges.subscribe((changedValueKeys) => {
      // check, is one of the like a news key, then reload the view, with new data
      const changedData = new Array<HiveAndKey>();
      for (const changedValueKey of changedValueKeys) {
        changedData.push(new HiveAndKey(StorageHive.Local, changedValueKey.toString()));
      }
      me._observerChanged.next(changedData);
    });
  }

  public get ObserveChanges(): Observable<HiveAndKey[]> {
    return this._observerChanged;
  }

  /** Wert wird zum Schlüssel gesetzt, bzw. aktualiesiert
   * @param hive Bereich
   * @param key Schlüssel
   * @param value Wert
   */
  public async set(hive: StorageHive, key: LocalKeys, value: any): Promise<void> {
    const me = this;
    return new Promise<void>(async (resolve, reject) => {
      try {
        switch (hive) {
          case StorageHive.Local:
            const val = new ValueStorageDataModel();
            val.key = key;
            val.value = value;
            await me._localValueStorageObsServ.SetObject(val);
            resolve();
            break;
          default:
            me._coreLogService.error("comEnvironmentStorage set", "Unknown hive", hive);
            reject();
            break;
        }
      } catch (ex) {
        me._coreLogService.error("comEnvironmentStorage set", "Ein Wert konnte nicht gesetzt werden", hive, key, value, ex);
        reject();
      }
    });
  }

  /** Wert zum Schlüssel widr gelesen
   * @param hive Bereich
   * @param key Schlüssel
   * @returns value or undefined
   */
  public async get(hive: StorageHive, key: LocalKeys): Promise<any> {
    const me = this;
    return new Promise<any>(async (resolve, reject) => {
      try {
        switch (hive) {
          case StorageHive.Local:
            try {
              const val = await me._localValueStorageObsServ.GetObject(key);
              if (!Utils.IsNull(val)) {
                resolve(val.value);
              } else {
                resolve(undefined);
              }
            } catch (error) {
              me._coreLogService.error("comEnvironmentStorage get", "_localValueStorageObsServ.GetObject", key, error);
              reject();
            }
            break;
          default:
            me._coreLogService.error("comEnvironmentStorage get", "Unknown hive", hive);
            reject();
            break;
        }
      } catch (ex) {
        this._coreLogService.error("comEnvironmentStorage get", "Ein Wert konnte nicht gelesen werden", hive, key, ex);
        reject();
      }
    });
  }

  /** Der Wert wird entfernt (auf undefined gesetzt)
   * @param hive Bereich
   * @param key Schlüssel
   */
  public async remove(hive: StorageHive, key: LocalKeys): Promise<void> {
    const me = this;
    return new Promise<void>(async (resolve, reject) => {
      try {
        resolve(await me.set(hive, key, undefined));
      } catch (ex) {
        this._coreLogService.error("comEnvironmentStorage remove", "Ein Wert konnte nicht entfernt werden", hive, key, ex);
        reject();
      }
    });
  }

  /** Alle Werte werden entfernen (auf undefined gesetzt)
   * Achtung: storeVersion und versionData sind davon ausgeshlossen
   * @param hive Bereich
   */
  public async removeAll(hive: StorageHive): Promise<void> {
    const me = this;
    return new Promise<void>(async (resolve, reject) => {
      try {
        switch (hive) {
          case StorageHive.Local:
            try {
              const values = await me._localValueStorageObsServ.GetObjectsByFilter((val) => typeof (val.value) != "undefined" && val.key != LocalKeys.storeVersion && val.key != LocalKeys.versionData);
              values.forEach((val) => { val.value = undefined; });
              resolve(me._localValueStorageObsServ.SetObjects(values));
            } catch (error) {
              me._coreLogService.error("comEnvironmentStorage removeAll", "_localValueStorageObsServ.GetObjectsByFilter", error);
              reject();
            }
            break;
          default:
            me._coreLogService.error("comEnvironmentStorage removeAll", "Unknown hive", hive);
            reject();
            break;
        }
      } catch (ex) {
        this._coreLogService.error("comEnvironmentStorage removeAll", "Alle Werte konnten nicht entfernt werden", hive, ex);
        reject();
      }
    });
  }

  ngOnDestroy(): void {
    const me = this;
    if (me.localValueStorageChangesSubscriber) {
      me.localValueStorageChangesSubscriber.unsubscribe();
    }
  }
}
